/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Divider, Typography } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import AcademicTermsCourseCardRenderer from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/AcademicTermsCourseCardRenderer';
import CreditsInfo from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/CreditsInfo';
import PlanUpdatedNotification from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/PlanUpdatedNotification';
import TermStatusTag from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/TermStatusTag';
import UnknownAvailabilityNotification from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/UnknownAvailabilityNotification';
import { useAddCoursesToTermDialog } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/AddCoursesToTermDialogContext';
import type { TermStatus } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/academicTermTypes';
import { useTermStore } from 'bundles/degree-home/components/v3-dpm/context/TermStoreContext';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { checkCourseHasUnknownAvailability, now } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import type { CreditsSummary } from 'bundles/degree-home/components/v3-dpm/types/gradeTypes';

import _t from 'i18n!nls/degree-home';

type Props = {
  termName: string;
  courses: PersonalizedSegmentCourse[];
  termStatus: TermStatus;
  creditSummary: CreditsSummary;
  isTermPlanAutoCorrected?: boolean; // TODO: [https://coursera.atlassian.net/browse/CPM-2279] make this prop required when adding the "plan updated" notification for past terms
  explicitTermId: string | undefined;
  estimatedTermId?: string;
};

const styles = {
  flexRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);

    &:last-of-type {
      margin-bottom: 0;
    }
  `,
  header: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  card: css`
    padding: var(--cds-spacing-300);
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-50);
    border-radius: var(--cds-spacing-100);
    margin-bottom: 0;
  `,
  unknownAvailabilityNotification: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  addCoursesBtnContainer: (isZeroCourseState: boolean) => css`
    display: flex;
    flex-direction: column;
    margin-top: ${isZeroCourseState ? 'var(--cds-spacing-200)' : 'var(--cds-spacing-100)'};
    gap: var(--cds-spacing-200);
  `,
  addCoursesButton: css`
    margin: 0;
    width: fit-content;
  `,
};

const AcademicTermsCard = ({
  termStatus,
  creditSummary,
  termName,
  courses,
  estimatedTermId,
  explicitTermId,
  isTermPlanAutoCorrected = false,
}: Props) => {
  const { inProgress, completed, planned } = creditSummary;

  const isTermStatusComplete = termStatus === 'COMPLETE';

  const isZeroCourseState = !courses?.length;

  const { terms } = useTermStore();
  const { openAddCoursesDialog } = useAddCoursesToTermDialog();
  const [isAutoUpdateNotificationDismissed, setIsAutoUpdateNotificationDismissed] = React.useState<boolean>(false);

  if (isTermStatusComplete && !courses.length) {
    return null;
  }

  const hasCoursesWithUnknownAvailability = courses.some((course) =>
    checkCourseHasUnknownAvailability(course.segmentCourse)
  );

  const currentTermEntry = terms?.[explicitTermId ?? ''];
  // only display the "Add courses" button if one of the followings is true
  // 1. the term is a future term
  // 2. the term is in-progress and registration has not ended (not open yet or still ongoing)
  const shouldDisplayAddCoursesButton =
    termStatus === 'NOT_STARTED' ||
    (termStatus === 'IN_PROGRESS' &&
      currentTermEntry?.registrationEndDate &&
      currentTermEntry.registrationEndDate > now());

  return (
    <div css={styles.card}>
      <div css={styles.header}>
        <div css={styles.flexRow}>
          <Typography variant="h2semibold">{termName}</Typography>
          <TermStatusTag termStatus={termStatus} />
        </div>
        <div css={styles.flexRow}>
          <CreditsInfo
            termStatus={termStatus}
            creditsEarned={completed}
            creditsInProgress={inProgress}
            creditsPlanned={planned}
          />
        </div>
      </div>
      {isTermPlanAutoCorrected && !isAutoUpdateNotificationDismissed && (
        <PlanUpdatedNotification onDismiss={() => setIsAutoUpdateNotificationDismissed(true)} />
      )}
      {hasCoursesWithUnknownAvailability && (
        <UnknownAvailabilityNotification styles={styles.unknownAvailabilityNotification} />
      )}
      <AcademicTermsCourseCardRenderer
        termId={estimatedTermId}
        termName={termName}
        termStatus={termStatus}
        courses={courses}
      />
      {shouldDisplayAddCoursesButton && (
        <div css={styles.addCoursesBtnContainer(isZeroCourseState)}>
          {!isZeroCourseState && <Divider color="light" />}
          <Button
            css={styles.addCoursesButton}
            variant="ghost"
            icon={<AddIcon size="small" />}
            iconPosition="before"
            edgeAlign="start"
            size="small"
            onClick={() => openAddCoursesDialog(estimatedTermId ?? '')}
          >
            {_t('Add courses')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AcademicTermsCard;
